import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import axios from "axios";
import { useAuth } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2, FileText, CheckCircle, Sparkles, AlertCircle, Upload, Pencil, X, Files, Building2 } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  if (!value && value !== 0) return "";
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const CATEGORIES = [
  'Carburant',
  'Fournitures',
  'Services',
  'Abonnement',
  'Maintenance',
  'Autre'
];

export const SupplierOCRUploadModal = ({ open, onOpenChange, onSuccess }) => {
  const { token } = useAuth();
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedData, setEditedData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  // Batch mode
  const [files, setFiles] = useState([]);
  const [batchMode, setBatchMode] = useState(false);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0 });
  const [batchResults, setBatchResults] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const pdfFiles = acceptedFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    
    if (pdfFiles.length > 1) {
      setBatchMode(true);
      setFiles(pdfFiles);
      setFile(null);
    } else if (pdfFiles.length === 1) {
      setBatchMode(false);
      setFile(pdfFiles[0]);
      setFiles([]);
    }
    
    setExtractedData(null);
    setEditedData(null);
    setError(null);
    setSuccess(false);
    setBatchResults(null);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true
  });

  const removeFile = (index) => {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    if (newFiles.length === 1) {
      setBatchMode(false);
      setFile(newFiles[0]);
      setFiles([]);
    } else if (newFiles.length === 0) {
      setBatchMode(false);
    }
  };

  const handleExtract = async () => {
    if (!file) return;

    try {
      setExtracting(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/supplier-invoices/extract-preview`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 180000 // 180s (3 min) - OCR with retry can take longer
      });
      
      setExtractedData(res.data.extracted_data);
      setEditedData(res.data.extracted_data);
      toast.success("Données extraites avec succès !");
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Erreur lors de l'extraction OCR";
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!editedData || !file) return;

    try {
      setSaving(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/supplier-invoices/upload-with-ocr`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 180000 // 180s (3 min) - OCR with retry can take longer
      });
      
      setSuccess(true);
      toast.success(`Facture ${res.data.invoice.numero} créée !`);
      onSuccess?.();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || "Erreur lors de la création";
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleBatchUpload = async () => {
    if (files.length === 0) return;

    setBatchProcessing(true);
    setError(null);
    setBatchProgress({ current: 0, total: files.length });
    
    const results = {
      total: files.length,
      successful: 0,
      failed: 0,
      duplicates: 0,
      created: [],
      errors: [],
      skipped: []  // For duplicates
    };

    // Process files one by one to avoid timeout
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setBatchProgress({ current: i, total: files.length, currentFile: file.name });
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(`${API}/supplier-invoices/upload-with-ocr`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          },
          timeout: 180000 // 180s (3 min) per file - OCR with retry can take longer
        });
        
        if (res.data.success) {
          results.successful++;
          results.created.push({
            filename: file.name,
            invoice: res.data.invoice
          });
        } else {
          results.failed++;
          results.errors.push({
            filename: file.name,
            error: res.data.error || "Erreur inconnue"
          });
        }
      } catch (err) {
        const errorMsg = err.response?.data?.detail || err.message || "Erreur lors de l'extraction";
        const isDuplicate = errorMsg.includes("existe déjà") || errorMsg.includes("Doublon");
        const isTokenExpired = errorMsg.toLowerCase().includes("token expired") || 
                              errorMsg.toLowerCase().includes("not authenticated") ||
                              err.response?.status === 401;
        const isTimeout = err.code === 'ECONNABORTED' || errorMsg.includes('timeout');
        
        if (isTokenExpired) {
          // Token expired - stop batch and ask for re-login
          toast.error("Session expirée. Veuillez vous reconnecter et relancer l'import.");
          results.failed += (files.length - i); // Count remaining as failed
          for (let j = i; j < files.length; j++) {
            results.errors.push({
              filename: files[j].name,
              error: "Token expired"
            });
          }
          break; // Stop the batch
        } else if (isDuplicate) {
          results.duplicates++;
          results.skipped.push({
            filename: file.name,
            error: errorMsg
          });
        } else if (isTimeout) {
          // Timeout - the backend might still be processing
          // Mark as warning, not error
          results.failed++;
          results.errors.push({
            filename: file.name,
            error: "Timeout - vérifiez si la facture a été importée"
          });
          // Add extra delay after timeout to let backend finish
          await new Promise(resolve => setTimeout(resolve, 5000));
        } else {
          results.failed++;
          results.errors.push({
            filename: file.name,
            error: errorMsg
          });
        }
      }
      
      // Small delay between files to avoid rate limiting
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    setBatchProgress({ current: files.length, total: files.length, currentFile: '' });
    setBatchResults(results);
    setBatchProcessing(false);
    setError(null); // Clear any previous error
    
    if (results.successful > 0) {
      toast.success(`${results.successful} facture(s) créée(s)`);
      onSuccess?.();
    }
    if (results.duplicates > 0) {
      toast.info(`${results.duplicates} doublon(s) ignoré(s)`);
    }
    if (results.failed > 0 && results.successful === 0) {
      toast.error(`${results.failed} facture(s) en erreur`);
    } else if (results.failed > 0) {
      toast.warning(`${results.failed} facture(s) n'ont pas pu être importées`);
    }
  };

  const handleClose = () => {
    if (!extracting && !saving && !batchProcessing) {
      setFile(null);
      setFiles([]);
      setExtractedData(null);
      setEditedData(null);
      setError(null);
      setSuccess(false);
      setEditMode(false);
      setBatchMode(false);
      setBatchResults(null);
      onOpenChange(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <Building2 className="w-5 h-5 text-orange-600" />
            Import Factures Fournisseurs (OCR)
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {batchMode 
              ? `Importez ${files.length} factures fournisseurs. Délai de 3s entre chaque extraction.`
              : "Uploadez une facture fournisseur PDF pour extraction automatique."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Success State */}
          {success && !batchMode && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Facture créée avec succès !</h3>
              <Button onClick={handleClose}>Fermer</Button>
            </motion.div>
          )}

          {/* Batch Results */}
          {batchResults && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div className="text-center py-4">
                {batchResults.successful > 0 ? (
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                ) : (
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                )}
                <h3 className="text-lg font-semibold">Import terminé</h3>
                <p className="text-sm text-muted-foreground">
                  {batchResults.successful} / {batchResults.total} facture(s) créée(s)
                  {batchResults.duplicates > 0 && (
                    <span className="text-amber-600"> • {batchResults.duplicates} doublon(s) ignoré(s)</span>
                  )}
                </p>
              </div>
              <div className="max-h-60 overflow-y-auto space-y-2">
                {/* Successful imports */}
                {batchResults.created?.map((result, idx) => (
                  <div key={`success-${idx}`} className="p-3 rounded-lg border bg-green-50 border-green-200">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-medium truncate">{result.filename}</span>
                    </div>
                    {result.invoice && (
                      <p className="text-xs text-green-700 mt-1 ml-6">{result.invoice.numero} - {result.invoice.fournisseur}</p>
                    )}
                  </div>
                ))}
                {/* Duplicates (skipped) - shown in amber/orange */}
                {batchResults.skipped?.map((result, idx) => (
                  <div key={`skip-${idx}`} className="p-3 rounded-lg border bg-amber-50 border-amber-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-amber-600" />
                      <span className="text-sm font-medium truncate">{result.filename}</span>
                    </div>
                    <p className="text-xs text-amber-700 mt-1 ml-6">{result.error}</p>
                  </div>
                ))}
                {/* Failed imports */}
                {batchResults.errors?.map((result, idx) => (
                  <div key={`error-${idx}`} className="p-3 rounded-lg border bg-red-50 border-red-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                      <span className="text-sm font-medium truncate">{result.filename}</span>
                    </div>
                    <p className="text-xs text-red-700 mt-1 ml-6">{result.error}</p>
                  </div>
                ))}
              </div>
              <Button onClick={handleClose} className="w-full">Fermer</Button>
            </motion.div>
          )}

          {/* Batch Processing */}
          {batchProcessing && (
            <div className="text-center py-8 space-y-4">
              <div className="relative">
                <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
              </div>
              <div>
                <p className="text-lg font-semibold text-foreground">
                  Import en cours...
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {batchProgress.current} / {batchProgress.total} fichiers traités
                </p>
                {batchProgress.currentFile && (
                  <p className="text-xs text-primary font-medium mt-2 truncate max-w-xs mx-auto">
                    En cours : {batchProgress.currentFile}
                  </p>
                )}
              </div>
              <Progress value={(batchProgress.current / batchProgress.total) * 100} className="w-full h-3" />
              <p className="text-xs text-muted-foreground">
                Extraction OCR de chaque facture en cours...
              </p>
            </div>
          )}

          {/* Dropzone */}
          {!success && !extractedData && !extracting && !batchResults && !batchProcessing && (
            <>
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  isDragActive ? 'border-orange-500 bg-orange-50' : 
                  (file || files.length) ? 'border-green-500 bg-green-50' : 
                  'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                }`}
              >
                <input {...getInputProps()} />
                <div className="space-y-3">
                  <div className={`w-14 h-14 mx-auto rounded-xl flex items-center justify-center ${(file || files.length) ? 'bg-green-100' : 'bg-orange-100'}`}>
                    {(file || files.length) ? (files.length > 1 ? <Files className="w-7 h-7 text-green-600" /> : <FileText className="w-7 h-7 text-green-600" />) : <Upload className="w-7 h-7 text-orange-600" />}
                  </div>
                  {file && !batchMode ? (
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground mt-1">Cliquez ou glissez pour changer</p>
                    </div>
                  ) : files.length > 0 ? (
                    <div>
                      <p className="text-sm font-medium">{files.length} fichiers sélectionnés</p>
                      <p className="text-xs text-muted-foreground mt-1">Cliquez pour ajouter d'autres fichiers</p>
                    </div>
                  ) : (
                    <div>
                      <p className="font-medium">{isDragActive ? 'Déposez les PDF ici' : 'Glissez vos factures fournisseurs PDF'}</p>
                      <p className="text-xs text-muted-foreground mt-1">ou cliquez pour parcourir</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Files list */}
              {batchMode && files.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Fichiers ({files.length})</p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {files.map((f, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                        <FileText className="w-4 h-4 text-gray-500" />
                        <span className="text-sm truncate flex-1">{f.name}</span>
                        <button onClick={() => removeFile(idx)} className="p-1 hover:bg-gray-200 rounded">
                          <X className="w-4 h-4 text-gray-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    Délai de 3 secondes entre chaque facture pour éviter la surcharge API.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          {!success && !extractedData && !extracting && !batchResults && !batchProcessing && (
            <>
              {file && !batchMode && (
                <Button onClick={handleExtract} className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Extraire les données
                </Button>
              )}
              {batchMode && files.length > 0 && (
                <Button onClick={handleBatchUpload} className="w-full bg-orange-600 hover:bg-orange-700 text-white h-12">
                  <Files className="w-5 h-5 mr-2" />
                  Importer {files.length} factures
                </Button>
              )}
            </>
          )}

          {/* Loading */}
          {extracting && (
            <div className="text-center py-8">
              <Loader2 className="w-10 h-10 animate-spin text-orange-600 mx-auto mb-4" />
              <p className="text-lg font-medium">Analyse en cours...</p>
              <p className="text-sm text-muted-foreground mt-1">Extraction OCR de la facture</p>
            </div>
          )}

          {/* Error */}
          {error && !extracting && !batchProcessing && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-100">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-red-800">Erreur</p>
                  <p className="text-sm text-red-600 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Extracted Data Preview */}
          {!success && extractedData && !extracting && !batchMode && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium">Données extraites</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => setEditMode(!editMode)}>
                  <Pencil className="w-3 h-3 mr-1" />
                  {editMode ? 'Aperçu' : 'Modifier'}
                </Button>
              </div>

              {extractedData.numero_exists && (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    Une facture <strong>{editedData?.numero}</strong> existe déjà.
                  </p>
                </div>
              )}

              {editMode ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Numéro</Label>
                    <Input value={editedData?.numero || ''} onChange={(e) => handleInputChange('numero', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Fournisseur</Label>
                    <Input value={editedData?.fournisseur || ''} onChange={(e) => handleInputChange('fournisseur', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Date</Label>
                    <Input type="date" value={editedData?.date || ''} onChange={(e) => handleInputChange('date', e.target.value)} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Catégorie</Label>
                    <Select value={editedData?.categorie || 'Autre'} onValueChange={(v) => handleInputChange('categorie', v)}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total HT</Label>
                    <Input type="number" step="0.01" value={editedData?.total_ht || ''} onChange={(e) => handleInputChange('total_ht', parseFloat(e.target.value))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">TVA</Label>
                    <Input type="number" step="0.01" value={editedData?.total_tva || ''} onChange={(e) => handleInputChange('total_tva', parseFloat(e.target.value))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total TTC</Label>
                    <Input type="number" step="0.01" value={editedData?.total_ttc || ''} onChange={(e) => handleInputChange('total_ttc', parseFloat(e.target.value))} className="mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Statut</Label>
                    <Select value={editedData?.statut_paiement || 'En attente'} onValueChange={(v) => handleInputChange('statut_paiement', v)}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="En attente">En attente</SelectItem>
                        <SelectItem value="Payée">Payée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs uppercase text-muted-foreground mb-1">Numéro</p>
                      <p className="text-sm font-mono font-medium text-orange-600">{editedData?.numero}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs uppercase text-muted-foreground mb-1">Catégorie</p>
                      <Badge variant="outline">{editedData?.categorie || 'Autre'}</Badge>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs uppercase text-muted-foreground mb-1">Fournisseur</p>
                    <p className="text-sm font-medium">{editedData?.fournisseur}</p>
                  </div>

                  <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <p className="text-xs uppercase text-muted-foreground mb-1">Total HT</p>
                        <p className="text-lg font-mono font-medium">{formatCurrency(editedData?.total_ht)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground mb-1">TVA</p>
                        <p className="text-lg font-mono text-muted-foreground">{formatCurrency(editedData?.total_tva)}</p>
                      </div>
                      <div>
                        <p className="text-xs uppercase text-muted-foreground mb-1">Total TTC</p>
                        <p className="text-lg font-mono font-bold text-orange-600">{formatCurrency(editedData?.total_ttc)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <Button
                onClick={handleSave}
                disabled={saving || extractedData.numero_exists}
                className="w-full bg-green-600 hover:bg-green-700 text-white h-12"
              >
                {saving ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Création...</>
                ) : (
                  <><CheckCircle className="w-5 h-5 mr-2" />Créer la facture</>
                )}
              </Button>
            </motion.div>
          )}

          {/* Cancel */}
          {!success && !batchResults && (
            <Button variant="outline" onClick={handleClose} disabled={extracting || saving || batchProcessing} className="w-full">
              Annuler
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupplierOCRUploadModal;
